import {
  Grid,
  Typography,
  Button,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useState, useRef } from "react";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useTheme } from "@mui/material/styles";
import { associateLawsuitLegalFile } from "../../services/LawsuitService";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DynamicExcelHelpModal from "../General/DynamicExcelHelpModal";
import Tooltip from "@mui/material/Tooltip";
import CardHeader from "../General/CardHeader";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";
import FileUploadBox from "../General/FileUploadBox";
import GlassCard from "../General/GlassCard";
import { useToast } from "../../context/ToastContext";

export default function ChargeLawsuitLegalFile() {
  const { showToast } = useToast();
  const theme = useTheme();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ file: "" });
  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [actionType, setActionType] = useState("");
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openHelp, setOpenHelp] = useState(false);

  const handleOpenHelp = () => setOpenHelp(true);
  const handleCloseHelp = () => setOpenHelp(false);

  // Adaptado para recibir el archivo directamente desde FileUploadBox
  const handleFileSelect = (file) => {
    if (file) {
      const validExtensions = ["xls", "xlsx"];
      const fileExtension = file.name.split(".").pop().toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setErrors({
          ...errors,
          file: "Solo se permiten archivos .xls o .xlsx",
        });
        setSelectedFile(null);
        setForm({ ...form, file: "" });
        return;
      }

      setErrors({ ...errors, file: "" });
      setSelectedFile(file);
      setForm({ ...form, file: file.name });
    }
  };

  const handleConfirmUpdate = () => {
    setOpenConfirm(false);
    handleSubmit();
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setErrors({});
    setLoading(true);

    if (!actionType) {
      showToast("Debe seleccionar una acción.", "info")
      setLoading(false);
      return;
    }

    if (!selectedFile) {
      showToast("Debe seleccionar un archivo.", "info")
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("action", actionType);

    try {
      const response = await associateLawsuitLegalFile(formData);

      showToast(response.data.message, "success")
      setForm({ file: "" });
      setSelectedFile(null);
      setErrors({});

      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      showToast(
        error.response?.data?.message || "Ocurrió un error inesperado.",
      "error")
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (actionType === "associate" || actionType === "detach") {
      setOpenConfirm(true);
    } else {
      handleSubmit();
    }
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>

        <GlassCard>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Ver requisitos del Excel" arrow>
              <HelpOutlineIcon
                sx={{
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  "&:hover": { color: theme.palette.primary.dark },
                }}
                onClick={handleOpenHelp}
              />
            </Tooltip>
          </Box>

          <CardHeader
            icon={<AssignmentIcon />}
            title="Asociar Títulos a Expedientes Legales"
            description="Actualizar títulos que no tienen expedientes asociados."
          />

          <Box component="form" onSubmit={handleFormSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <FormControl fullWidth>
                  <InputLabel>Acción</InputLabel>
                  <Select
                    value={actionType}
                    onChange={(e) => setActionType(e.target.value)}
                    label="Acción"
                  >
                    <MenuItem value="">SELECCIONAR UNA OPCION...</MenuItem>
                    <MenuItem value="associate">ASOCIAR TÍTULOS</MenuItem>
                    <MenuItem value="detach">DESASOCIAR TÍTULOS</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FileUploadBox
                  selectedFile={selectedFile}
                  onFileSelect={handleFileSelect}
                  accept=".xls,.xlsx"
                  placeholder="Seleccionar Excel"
                  helperText="O arrastrá y soltá el archivo aquí (.xls, .xlsx)"
                />

                {errors.file && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {errors.file}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                  disabled={loading}
                 
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : actionType === "associate" ? (
                    "Asociar Títulos"
                  ) : actionType === "detach" ? (
                    "Desasociar Títulos"
                  ) : (
                    "Ejecutar Acción"
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </GlassCard>
      </Grid>

      <ConfirmDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onConfirm={handleConfirmUpdate}
        title={
          actionType === "detach"
            ? "Confirmar desasociación"
            : "Confirmar asociación"
        }
        description={
          actionType === "detach"
            ? "¿Estás seguro de que quieres desasociar estos títulos? Esta acción no se puede deshacer."
            : "¿Estás seguro de que quieres asociar estos títulos? Esta acción no se puede deshacer."
        }
        confirmText={actionType === "detach" ? "Desasociar" : "Asociar"}
        cancelText="Cancelar"
        confirmColor={actionType === "detach" ? "warning" : "primary"}
        loading={loading}
      />

      <DynamicExcelHelpModal
        open={openHelp}
        onClose={handleCloseHelp}
        title="Requisitos del archivo Excel"
        description="El archivo debe ser formato .xls o .xlsx."
        columns={["Códigos de Títulos", "Código de Expediente"]}
        example={["Inm/1234/2025", "12345678"]}
      />
    </Grid>
  );
}
